import { zodResolver } from "@hookform/resolvers/zod"
import { ArrowLeftIcon, BanknoteIcon, CheckIcon, Loader2Icon } from "lucide-react"
import type * as React from "react"
import { useEffect } from "react"
import { Controller, useForm, type Resolver } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Link, useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"
import { z } from "zod"
import {
  CustomerCreateSchema,
  CustomerUpdateSchema,
  GST_STATE_CODE_LIST,
  panFromGstin,
} from "@steelbill/types"
import {
  useCustomer,
  useSaveCustomer,
  useValidateGstin,
} from "@/features/customers/use-customer"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { useAuth } from "@/features/auth/auth-context"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"
import { ApiError } from "@/lib/api-error"
import { routes } from "@/routes/paths"

/*
 * THE 44px CONDITION WAS WITHDRAWN BY THE OWNER ON 2026-08-16, deliberately and
 * not by tidying — the comment that stood here asked not to be deleted silently,
 * so this is the record.
 *
 * These Selects replaced native `<select>`s, and the migration was granted on
 * condition the items stayed a real 44px touch target, because a phone's OS
 * wheel is big and reliably hittable and a cramped in-page listbox is not. That
 * reasoning was sound in the abstract and wrong in this app: every other menu
 * here — Quick Create in the sidebar — is a `DropdownMenu` at ~28px, which the
 * owner uses daily without complaint. Against it the 44px Select looked bulky,
 * which he said twice, comparing the two directly.
 *
 * So the rule is now CONSISTENCY, not a number: `SelectItem` matches
 * `DropdownMenuItem`, and the height lives in components/ui/select.tsx. If the
 * targets are ever raised, both are raised in the same change — one of them
 * alone is the bug.
 */

/*
 * AND A HALF-READ STATE NAME IS A CORRECTNESS BUG, NOT A COSMETIC ONE. The OS
 * wheel sized itself to the text; `SelectContent` is `w-(--anchor-width)` with
 * `overflow-x-hidden`, and `SelectItem`'s text div is `whitespace-nowrap`, so
 * "Dadra and Nagar Haveli and Daman and Diu (26)" — 44 characters — would be
 * clipped against a trigger the width of a form field. The state decides
 * CGST+SGST versus IGST (§6), and a wrong split on an issued invoice can only
 * be corrected by a credit note (§17.3), so the popup is allowed to grow past
 * the trigger and the label is allowed to wrap. `[&>div]:` reaches the
 * `Select.ItemText` div specifically, which is where the `nowrap` lives.
 */
const WIDE_CONTENT = "w-auto min-w-(--anchor-width) max-w-[min(90vw,28rem)]"
const WRAPPING_ITEM = "whitespace-normal [&>div]:whitespace-normal"

/*
 * AND THE TRIGGER MUST SAY THE STATE'S NAME, NOT ITS CODE.
 *
 * `Select.Value` does NOT read the chosen item's text. It resolves a label
 * through the root's `items` map (`internals/resolveValueLabel.js`), and with no
 * `items` it falls back to `stringifyAsLabel(value)` — the raw string. The
 * native <select> showed the chosen <option>'s words for free; a `<SelectValue/>`
 * left to itself would render "27" on the field that decides CGST+SGST versus
 * IGST, which is unreadable and, worse, unverifiable at a glance.
 *
 * One map, passed to the root AND rendered as the items, so the trigger and the
 * list cannot disagree about what a code is called. Module-level because
 * `GST_STATE_CODE_LIST` is frozen and these labels are proper nouns — not
 * translated, so nothing here depends on `t`.
 *
 * Note the order is "{name} ({code})", which is the REVERSE of the settings and
 * quick-add pickers ("{code} — {name}"). That predates this change; it is left
 * alone rather than quietly harmonised on a tax field.
 */
const STATE_LABELS: Record<string, string> = Object.fromEntries(
  GST_STATE_CODE_LIST.map((state) => [state.code, `${state.name} (${state.code})`]),
)

/**
 * Add or edit a customer (§7).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE SAME ZOD SCHEMA THE SERVER VALIDATES WITH — AND THAT MEANS A DIFFERENT
 * ONE PER VERB. `CustomerCreateSchema` is what `POST /customers` runs and
 * `CustomerUpdateSchema` is what `PUT /customers/:id` runs, so the message
 * shown beside a field is the message the API would have produced. Two
 * hand-written validators drift within a month, and the one that drifts is
 * always the client's — which then rejects something the server would have
 * accepted, with no way for the owner to tell.
 *
 * THIS SCREEN USED THE *CREATE* SCHEMA FOR BOTH, which was that same drift by a
 * shorter route. The update schema makes every optional field CLEARABLE (an
 * emptied box becomes `null`, which is how you erase a stored value); the create
 * schema has no such notion, so an emptied PAN box submitted `""` against
 * `PanSchema.length(10)` and the save was refused with "PAN must be exactly 10
 * characters" — on a field the owner was deliberately clearing, against a server
 * that would have accepted it. The same trap sat on every other optional field.
 *
 * GSTIN OR STATE, AND THE SCHEMA ENFORCES IT. A registered customer's state is
 * DERIVED from the first two digits of the GSTIN; an unregistered one has no
 * GSTIN, so the state must be chosen by hand. Getting it wrong is not cosmetic:
 * the state decides CGST+SGST versus IGST on every bill (§6), and a wrong tax
 * split on an issued invoice can only be corrected by a credit note (§17.3).
 *
 * EDITING NEVER CHANGES A DOCUMENT ALREADY ISSUED. Every invoice froze its own
 * copy of the buyer when it was saved (§4), so correcting a spelling here fixes
 * the next bill and leaves the customer's existing ones exactly as they hold
 * them. The screen says so, because "will this change my old bills?" is the
 * first thing the owner will wonder.
 * ─────────────────────────────────────────────────────────────────────────────
 */
/** The fields this form holds — the create schema's INPUT side. */
type CustomerFormValues = z.input<typeof CustomerCreateSchema>

/**
 * The resolver for the verb in play.
 *
 * Each branch calls `zodResolver` with a CONCRETE schema — passing a ternary
 * union gives the overloads nothing to resolve against. The cast is the same one
 * SettingsScreen uses: `clearable`'s `preprocess` widens the input type to
 * `unknown`, so the static type of the resolver cannot line up with the form's
 * field types even though the runtime validation is exactly right.
 */
function customerResolver(isEdit: boolean): Resolver<CustomerFormValues> {
  return (
    isEdit ? zodResolver(CustomerUpdateSchema) : zodResolver(CustomerCreateSchema)
  ) as unknown as Resolver<CustomerFormValues>
}

export function CustomerEditorScreen(): React.JSX.Element {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { can } = useAuth()
  const { id } = useParams<{ id: string }>()
  const isEdit = id !== undefined

  /*
   * §17.5's statement, reachable FROM THE CUSTOMER (owner's ask, 2026-08-17).
   *
   * It was reachable only from `PaymentsCard` on an invoice, so answering "what
   * does Lupin owe me?" meant first finding one of Lupin's bills — the question
   * is asked about the customer and the app made you start from a document.
   *
   * GATED ON `payment.view`, NOT `customer.view`. The statement route carries
   * that capability (routes/index.tsx), so gating this button on the screen's
   * own capability would offer a door that closes in your face. It is also why
   * this is not simply always shown.
   *
   * NOT SHOWN ON A NEW CUSTOMER: there is no id to point at, and nothing owed by
   * somebody who does not exist yet.
   */
  const canOpenStatement = isEdit && id !== undefined && can("payment.view")

  /*
   * ═══════════════════════════════════════════════════════════════════════════
   * MAY THIS PERSON SAVE? — AND THE ANSWER IS NOT THE CAPABILITY THAT OPENED
   * THE SCREEN.
   *
   * `/customers/:id` is mounted behind `customer.view` (routes/index.tsx) and
   * `/customers/new` behind `customer.create`, but the SAVE is `PUT`/`POST`
   * `/customers`, which the server gates on `customer.edit` / `customer.create`
   * (routes/customers.ts). Until the CA had a login those never came apart —
   * every role holding `customer.view` also held `customer.edit` — so a screen
   * that asked only "may you look?" and then rendered a full editing form was
   * latent rather than wrong. Opening ACCOUNTANT is exactly what turned it into
   * a live defect: `customer.edit` lives in `AUTHOR_DOCUMENTS`, which her row
   * does not spread.
   *
   * WHAT SHE GOT: a screen headed "Customer details" with the name, the GSTIN,
   * the PAN, the state, the address and the phone all live, and a Save button
   * disabled by nothing but `save.isPending`. Pressing it produced a 403 and a
   * red "Could not save" — the first thing on the screen to mention that she
   * could not do this. It also wrote a DENIAL into `audit_log` (§2.1 audits
   * denied attempts), so the audit window the owner asked for would fill with
   * refusals she never chose to attempt.
   *
   * THE SCREEN STAYS REACHABLE, READ-ONLY. Routing `/customers/:id` behind
   * `customer.edit` was the other option and it is the wrong one: she files
   * returns against these GSTINs and needs to be able to look one up. A register
   * whose every row is a door into a form she cannot save is the defect; a
   * register whose rows open a record she can read is the feature.
   *
   * ASKED AS A CAPABILITY, NEVER AS `role === "ACCOUNTANT"` (§2.1) — this is
   * precisely the place that branch would have been written, and it would be
   * wrong the day a fourth role holds one of these and not the other.
   * ═══════════════════════════════════════════════════════════════════════════
   */
  const canSave = isEdit ? can("customer.edit") : can("customer.create")

  const existing = useCustomer(id)
  const save = useSaveCustomer(id)
  const gstinCheck = useValidateGstin()

  /*
   * THE FORM'S SHAPE IS THE CREATE SCHEMA'S, EXPLICITLY — the resolver varies by
   * verb, and a conditional resolver gives TypeScript nothing to infer the field
   * types from. The create shape is the right one to state: it is the superset
   * (the update schema is the same fields, `.partial()` and clearable), so every
   * field name and type below is checked against it.
   */
  const form = useForm<CustomerFormValues>({
    /*
     * The verb decides the schema: a PUT is partial and clearable, a POST is
     * not — see the note above the component. Cast because `clearable`'s
     * `preprocess` widens the INPUT type to `unknown`, which is what the form
     * actually holds mid-edit; the resolver still validates with the real
     * schema at runtime, which is the part that matters.
     */
    resolver: customerResolver(isEdit),
    mode: "onTouched",
    defaultValues: {
      name: "",
      gstin: undefined,
      pan: undefined,
      stateCode: undefined,
      addressLine1: undefined,
      addressLine2: undefined,
      city: undefined,
      pincode: undefined,
      phone: undefined,
      email: undefined,
      vendorCode: undefined,
      defaultContactName: undefined,
    },
  })

  // Fill the form once the row arrives. `reset` rather than per-field setValue
  // so the form's dirty state starts clean and "unsaved changes" stays honest.
  useEffect(() => {
    const customer = existing.data
    if (customer === undefined) return
    form.reset({
      name: customer.name,
      gstin: customer.gstin ?? undefined,
      pan: customer.pan ?? undefined,
      stateCode: customer.stateCode,
      addressLine1: customer.addressLine1 ?? undefined,
      addressLine2: customer.addressLine2 ?? undefined,
      city: customer.city ?? undefined,
      pincode: customer.pincode ?? undefined,
      phone: customer.phone ?? undefined,
      email: customer.email ?? undefined,
      vendorCode: customer.vendorCode ?? undefined,
      defaultContactName: customer.defaultContactName ?? undefined,
    })
  }, [existing.data, form])

  /*
   * TWO SOURCES, BECAUSE `watch` ALONE LIED. `form.watch("gstin")` was observed
   * returning "" after `form.reset` populated the field — the input visibly
   * held the GSTIN while everything derived from the watch (the PAN, the
   * derived-vs-typed branch) behaved as if it were blank. Falling back to the
   * LOADED ROW makes the edit screen immune to that subscription quirk: the
   * row is what reset filled the form from, so the two can only differ while
   * the user is actively retyping — exactly when watch does work.
   */
  const gstin = (form.watch("gstin") ?? "") || (existing.data?.gstin ?? "")
  /*
   * The GSTIN as characters, nothing else. Portals PRINT GSTINs with spaces
   * inside ("27ASU PB437 1K1ZA" on a real e-way bill), and a paste carries
   * them — plus whatever invisible characters a PDF copy adds. A strict
   * `length === 15` on the raw string made the derived PAN silently blank
   * while the input looked perfectly correct.
   */
  const gstinClean = gstin.toUpperCase().replace(/[^0-9A-Z]/g, "")

  /*
   * Check the GSTIN as soon as it is 15 characters, and fill the state from it.
   * Not on every keystroke: a partial GSTIN is always invalid, so checking one
   * would show "that does not look right" while the owner is still typing it.
   */
  useEffect(() => {
    const value = gstin.trim().toUpperCase()
    if (value.length !== 15) return

    gstinCheck.mutate(value, {
      onSuccess: (result) => {
        if (result.valid && result.stateCode !== undefined) {
          // The state is a FACT about the GSTIN, not a choice — set it and let
          // the select show it, disabled.
          form.setValue("stateCode", result.stateCode as never, { shouldValidate: true })
        }
      },
    })
    // `gstinCheck` is a stable mutation object; including it would re-fire the
    // check on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gstin, form])

  const registered = gstin.trim().length === 15 && (gstinCheck.data?.valid ?? false)

  const onSubmit = form.handleSubmit((values) => {
    save.mutate(values as Record<string, unknown>, {
      onSuccess: (customer) => {
        toast.success(t("customer.saved"))
        void navigate(routes.customer(customer.id), { replace: true })
      },
    })
  })

  if (isEdit && existing.isPending) return <EditorSkeleton />

  return (
    <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
      <Link
        to={routes.customers}
        className="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1.5 text-sm"
      >
        <ArrowLeftIcon className="size-4" />
        {t("nav.customers")}
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          {/* The heading tells the truth about what this screen is FOR. "Change
            * what you know about them" over a form nobody can submit is the
            * sentence that makes a 403 feel like a bug in the app. */}
          <h1 className="text-2xl font-semibold tracking-tight">
            {!canSave
              ? t("customer.viewTitle")
              : isEdit
                ? t("customer.editTitle")
                : t("customer.addTitle")}
          </h1>
          <p className="text-muted-foreground text-sm">
            {!canSave
              ? t("customer.viewSubtitle")
              : isEdit
                ? t("customer.editSubtitle")
                : t("customer.addSubtitle")}
          </p>
        </div>

        {canOpenStatement ? (
          /*
           * The same control PaymentsCard offers, deliberately word for word and
           * icon for icon: one destination should not have two names depending
           * on which screen sent you.
           */
          <Button
            variant="outline"
            nativeButton={false}
            render={<Link to={routes.customerStatement(id)} />}
          >
            <BanknoteIcon />
            {t("payment.openStatement")}
          </Button>
        ) : null}
      </header>

      {save.isError ? (
        <Alert variant="destructive">
          <AlertTitle>{t("err.couldNotSave")}</AlertTitle>
          <AlertDescription>
            {/* The server's own sentence — it is the only thing that can say
              * "GSTIN 27… already belongs to customer #4 (Lupin)". */}
            {save.error instanceof ApiError ? save.error.message : t("err.somethingWentWrong")}
          </AlertDescription>
        </Alert>
      ) : null}

      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4 md:gap-6">
        {/*
          * ONE `fieldset`, NOT A `disabled` PROP ON EACH INPUT.
          *
          * A disabled fieldset disables every form control inside it — that is
          * plain HTML, not a component convention — so nothing on this screen
          * can be typed into, and, more importantly, NOTHING ADDED TO IT LATER
          * CAN BE EITHER. Per-field props are a list somebody has to remember to
          * extend, and the field they forget is the one that 403s.
          *
          * `display: contents` so the fieldset lays out as if it were not there
          * and the form's `flex-col gap-4` still applies to the cards directly.
          * Disabling is a semantic property of the element, untouched by how it
          * is displayed.
          *
          * The anchor-rendered Cancel/back button below sits OUTSIDE it — an
          * <a> is not a form control, so it would keep working either way, but
          * "the way out is not inside the thing that is switched off" is worth
          * being literal about.
          */}
        <fieldset disabled={!canSave} className="contents">
        <Card>
          <CardHeader>
            <CardTitle>{t("customer.whoSection")}</CardTitle>
            <CardDescription>{t("customer.gstinOptional")}</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <Field
              className="sm:col-span-2"
              label={t("lbl.customerName")}
              error={form.formState.errors.name?.message}
            >
              <Input {...form.register("name")} autoComplete="organization" />
            </Field>

            <Field label={t("lbl.gstin")} error={form.formState.errors.gstin?.message}>
              <Input
                {...form.register("gstin")}
                autoCapitalize="characters"
                spellCheck={false}
                maxLength={15}
                className="tabular-nums uppercase"
              />
              <GstinNote checking={gstinCheck.isPending} result={gstinCheck.data} gstin={gstin} />
            </Field>

            {/*
              * THE PAN, SHOWN BUT NEVER TYPED. A GSTIN is
              * <state><PAN><entity><Z><check>, so the PAN is characters 3–12.
              * Derived through the SHARED `panFromGstin` in `@steelbill/types`,
              * which is what the PDF uses too — this screen used to slice the
              * string itself, so the app and the printed invoice each had their
              * own copy of the rule. It is displayed here so "where is the PAN?" is
              * answered on the screen the question is asked at, and it is
              * read-only because a SECOND, typed copy of a fact the GSTIN
              * already carries is a copy that drifts — someone fixes a GSTIN
              * typo and a stale PAN keeps printing on legal documents.
              */}
            {gstin.trim() !== "" ? (
              <Field label={t("lbl.pan")} help={t("customer.panDerived")}>
                {/*
                  * `readOnly`, NOT `disabled`: disabled paints the value in
                  * placeholder-grey, and a real PAN that looks like a hint reads
                  * as "not filled in" — the exact complaint this field answers.
                  * Full-strength text on a muted ground says "a fact, not a box
                  * to type in".
                  */}
                <Input
                  value={panFromGstin(gstinClean) ?? ""}
                  readOnly
                  tabIndex={-1}
                  placeholder="—"
                  className="bg-muted text-foreground tabular-nums uppercase"
                />
              </Field>
            ) : (
              /*
               * NO GSTIN → a REAL PAN box (Rule 114B: a single sale over
               * ₹2,00,000 must quote the buyer's PAN even when they are
               * unregistered). The server refuses this field the moment a
               * GSTIN exists, so the two sources can never disagree.
               */
              <Field
                label={t("lbl.pan")}
                help={t("customer.panWhenUnregistered")}
                error={form.formState.errors.pan?.message}
              >
                <Input
                  {...form.register("pan")}
                  autoCapitalize="characters"
                  spellCheck={false}
                  maxLength={10}
                  className="tabular-nums uppercase"
                />
              </Field>
            )}

            <Field
              label={t("lbl.state")}
              /*
               * The <Label> now NAMES the control. This field used to be the one
               * on the screen where nothing was associated at all — `Field`
               * rendered a bare <Label> and the native select had no id — so a
               * screen reader announced "combo box" with no name. The trigger is
               * a <button role="combobox">, which is a labelable element, so
               * `htmlFor` + the trigger's id is a real association.
               */
              htmlFor="customer-state"
              error={form.formState.errors.stateCode?.message}
              help={registered ? undefined : t("customer.stateHelp")}
            >
              {/*
                * A CONTROLLER, NOT `register`. `form.register` hands back an
                * event-based `onChange` and a ref to a form element; Base UI's
                * Select root takes neither — it calls `onValueChange(value)`
                * with the value itself. Spreading `register` onto it would look
                * fine and silently never write to the form.
                *
                * The "" item is kept, exactly as the native placeholder option
                * was: choosing it writes "" and zod then says the state is
                * required for a customer with no GSTIN. Base UI reads value=""
                * as NO VALUE, so the trigger falls back to its placeholder —
                * which is deliberately the SAME string, so the control reads
                * identically whether Base UI considers "" selected or absent.
                */}
              <Controller
                control={form.control}
                name="stateCode"
                render={({ field }) => (
                  <Select
                    items={STATE_LABELS}
                    value={field.value ?? ""}
                    onValueChange={(value) => {
                      // `onValueChange` admits null as well as ""; both mean
                      // "no state chosen", which is "" to the form and to zod.
                      field.onChange(value == null ? "" : String(value))
                    }}
                  >
                    <SelectTrigger
                      id="customer-state"
                      className="w-full"
                      onBlur={field.onBlur}
                      /*
                       * LOCKED WHEN THE GSTIN DECIDED IT. The state is the first
                       * two digits of the GSTIN — letting it be overridden would
                       * allow a customer whose GSTIN says Gujarat to be billed as
                       * Maharashtra, i.e. CGST+SGST where IGST is due. It sits on
                       * the TRIGGER because that is the <button> the owner would
                       * otherwise tap; `form.setValue` from the GSTIN check still
                       * writes the value, which is the whole point.
                       */
                      disabled={registered}
                    >
                      <SelectValue placeholder={t("customer.stateChoose")} />
                    </SelectTrigger>
                    {/* 37 states is a long list in a popover where the OS wheel
                      * was full-height. Base UI's typeahead matches on the label
                      * as well as the value, so "mah" reaches Maharashtra
                      * without scrolling; `SelectContent` renders its own scroll
                      * arrows, so the rest is reachable either way. */}
                    <SelectContent className={WIDE_CONTENT}>
                      <SelectItem value="" className={WRAPPING_ITEM}>
                        {t("customer.stateChoose")}
                      </SelectItem>
                      {GST_STATE_CODE_LIST.map((state) => (
                        <SelectItem
                          key={state.code}
                          value={state.code}
                          className={WRAPPING_ITEM}
                        >
                          {STATE_LABELS[state.code]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </Field>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t("customer.addressSection")}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <Field
              className="sm:col-span-2"
              label={t("lbl.addressLine1")}
              error={form.formState.errors.addressLine1?.message}
            >
              <Input {...form.register("addressLine1")} autoComplete="address-line1" />
            </Field>
            <Field
              className="sm:col-span-2"
              label={t("lbl.addressLine2")}
              error={form.formState.errors.addressLine2?.message}
            >
              <Input {...form.register("addressLine2")} autoComplete="address-line2" />
            </Field>
            <Field label={t("lbl.city")} error={form.formState.errors.city?.message}>
              <Input {...form.register("city")} autoComplete="address-level2" />
            </Field>
            <Field label={t("lbl.pincode")} error={form.formState.errors.pincode?.message}>
              <Input
                {...form.register("pincode")}
                inputMode="numeric"
                maxLength={6}
                className="tabular-nums"
                autoComplete="postal-code"
              />
            </Field>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t("customer.contactSection")}</CardTitle>
            <CardDescription>{t("customer.contactHelp")}</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <Field
              label={t("lbl.contactName")}
              error={form.formState.errors.defaultContactName?.message}
            >
              <Input {...form.register("defaultContactName")} autoComplete="name" />
            </Field>
            <Field label={t("lbl.phone")} error={form.formState.errors.phone?.message}>
              <Input
                {...form.register("phone")}
                type="tel"
                inputMode="tel"
                className="tabular-nums"
                autoComplete="tel"
              />
            </Field>
            <Field label={t("lbl.email")} error={form.formState.errors.email?.message}>
              <Input {...form.register("email")} type="email" autoComplete="email" />
            </Field>
            <Field
              label={t("lbl.vendorCode")}
              error={form.formState.errors.vendorCode?.message}
              help={t("customer.vendorCodeHelp")}
            >
              <Input {...form.register("vendorCode")} spellCheck={false} />
            </Field>
          </CardContent>
        </Card>

        {isEdit ? <InUseCard id={id} /> : null}
        </fieldset>

        <div className="flex flex-wrap gap-2">
          {/* NOT RENDERED AT ALL rather than rendered disabled. A greyed-out
            * Save invites the reader to work out what would enable it; its
            * absence, under a heading that says this is a record to read, says
            * the true thing once. */}
          {canSave ? (
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? <Loader2Icon className="animate-spin" /> : <CheckIcon />}
              {t("btn.save")}
            </Button>
          ) : null}
          <Button
            type="button"
            variant="outline"
            nativeButton={false}
            render={<Link to={routes.customers} />}
          >
            {canSave ? t("btn.cancel") : t("btn.back")}
          </Button>
        </div>
      </form>
    </div>
  )
}

/** What the GSTIN check found — the state, or why it was refused. */
function GstinNote({
  checking,
  result,
  gstin,
}: {
  checking: boolean
  result: { valid: boolean; reason?: string; stateName?: string; supplyType?: string } | undefined
  gstin: string
}): React.JSX.Element | null {
  const { t } = useTranslation()
  const trimmed = gstin.trim()

  if (trimmed.length === 0) {
    return <p className="text-muted-foreground text-xs">{t("customer.gstinHelp")}</p>
  }
  if (trimmed.length < 15) {
    return <p className="text-muted-foreground text-xs">{t("customer.gstinIncomplete")}</p>
  }
  if (checking) return <p className="text-muted-foreground text-xs">{t("customer.gstinChecking")}</p>
  if (result === undefined) return null

  if (!result.valid) {
    return <p className="text-destructive text-xs">{t("customer.gstinNotValid")}</p>
  }

  return (
    <p className="text-muted-foreground text-xs">
      {t("customer.gstinResolvedState", { state: result.stateName ?? "" })}{" "}
      {/* Which taxes this customer's bills will carry — the consequence of the
        * state, stated where the decision is being made rather than discovered
        * on the first invoice. */}
      {result.supplyType === "INTER_STATE"
        ? t("customer.taxWillBeIgst")
        : t("customer.taxWillBeCgstSgst")}
    </p>
  )
}

/**
 * The archive switch. Separate card, at the bottom, because it is the one
 * control here that changes whether this customer appears in pickers.
 */
function InUseCard({ id }: { id: string }): React.JSX.Element {
  const { t } = useTranslation()
  const existing = useCustomer(id)
  const save = useSaveCustomer(id)

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("customer.inUse")}</CardTitle>
        <CardDescription>{t("customer.inUseHelp")}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-3">
          <Switch
            id="customer-active"
            checked={existing.data?.isActive ?? true}
            disabled={save.isPending || existing.isPending}
            onCheckedChange={(checked) => {
              // Sent on its own, not folded into the form's submit: archiving is
              // a decision, and burying it in "Save" would let a stray tap on
              // the switch ride along with an unrelated spelling correction.
              save.mutate({ isActive: checked })
            }}
          />
          <Label htmlFor="customer-active">{t("customer.inUse")}</Label>
        </div>
      </CardContent>
    </Card>
  )
}

function Field({
  label,
  htmlFor,
  error,
  help,
  className,
  children,
}: {
  label: string
  /**
   * The id of the control this field wraps, when there is one to point at.
   *
   * OPTIONAL, AND ADDITIVE ON PURPOSE. Every other caller here wraps an `Input`
   * carrying `{...form.register(name)}`, which supplies no id — omitting this
   * leaves those fields exactly as they were rather than inventing ids across a
   * screen this change has no business touching. The state picker passes it
   * because its trigger is a <button role="combobox"> that a screen reader
   * would otherwise announce unnamed.
   */
  htmlFor?: string
  error?: string
  help?: string
  className?: string
  children: React.ReactNode
}): React.JSX.Element {
  return (
    <div className={`flex flex-col gap-1.5 ${className ?? ""}`}>
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {error === undefined ? (
        help === undefined ? null : (
          <p className="text-muted-foreground text-xs">{help}</p>
        )
      ) : (
        <p className="text-destructive text-xs">{error}</p>
      )}
    </div>
  )
}

function EditorSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
      <Skeleton className="h-4 w-24" />
      <Skeleton className="h-8 w-56" />
      <Skeleton className="h-48 w-full" />
      <Skeleton className="h-40 w-full" />
    </div>
  )
}
